import { Component, DestroyRef, effect, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import {
	DashboardApiService,
	DashboardKpisDTO,
	MonthlyRevenue,
	PaymentMethodStat,
	RevenueBreakdownDTO,
	TopPlatform,
} from '@services/dashboard/dashboard-api.service';
import { DashboardFiltersService } from '@services/shared/dashboard-filters.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { SharedModule } from '@shared/modules/shared.module';
import { BRANDING } from 'app/core/constants/branding.constants';
import { Subscription } from 'rxjs';

@Component({
	selector: 'app-dashboard',
	imports: [SharedModule, ButtonComponent, SpinnerComponent],
	templateUrl: './dashboard.component.html',
	styleUrl: './dashboard.component.scss',
})
export class DashboardComponent {
	private readonly dashboardApi = inject(DashboardApiService);
	private readonly translocoService = inject(TranslocoService);

	// Period selector (state lives in DashboardFiltersService, shared with topbar)
	dashboardFilters = inject(DashboardFiltersService);
	readonly brandName = BRANDING.name;

	// Loading state
	isLoading = false;
	/** KPIs failed: show an error with retry instead of stale numbers or zeros */
	loadError = false;

	// KPI values
	totalRevenue = 0;
	totalOrders = 0;
	totalUsers = 0;
	avgTicket = 0;
	monthlyRevenue: MonthlyRevenue[] = [];
	topPlatforms: TopPlatform[] = [];
	paymentMethods: PaymentMethodStat[] = [];

	// Revenue breakdown
	revenueBreakdown: RevenueBreakdownDTO | null = null;
	showRevenueBreakdown = true;

	/** In-flight requests of the current period: a quick period change cancels them */
	private requests = new Subscription();

	constructor() {
		// Runs once on creation (first load) and again on every period change: a single load path,
		// it used to load twice on entry (effect + ngOnInit)
		effect(() => this.loadKpis(this.dashboardFilters.selectedPeriod()));
		inject(DestroyRef).onDestroy(() => this.requests.unsubscribe());
	}

	retry(): void {
		this.loadKpis(this.dashboardFilters.selectedPeriod());
	}

	private loadKpis(period: string): void {
		this.isLoading = true;
		this.loadError = false;
		this.requests.unsubscribe();
		this.requests = new Subscription();

		this.requests.add(
			this.dashboardApi.getKpis(period).subscribe({
				next: (data: DashboardKpisDTO) => {
					this.applyData(data);
					this.isLoading = false;
				},
				error: () => {
					this.loadError = true;
					this.isLoading = false;
				},
			}),
		);

		this.requests.add(
			this.dashboardApi.getRevenueBreakdown(period).subscribe({
				next: (data) => {
					this.revenueBreakdown = data;
				},
				error: () => {
					this.revenueBreakdown = null;
					this.showRevenueBreakdown = false;
				},
			}),
		);
	}

	private applyData(data: DashboardKpisDTO): void {
		this.totalRevenue = data.totalRevenue;
		this.totalOrders = data.totalOrders;
		this.totalUsers = data.totalUsers;
		this.avgTicket = data.avgTicket;
		this.monthlyRevenue = data.monthlyRevenue;
		this.topPlatforms = data.topPlatforms;
		this.paymentMethods = data.paymentMethods ?? [];
	}

	/** A year period charts its 12 months; the rest chart the last 6 */
	isYearPeriod(): boolean {
		return /^\d{4}$/.test(this.dashboardFilters.selectedPeriod());
	}

	getMaxRevenue(): number {
		if (!this.monthlyRevenue.length) return 1;
		return Math.max(...this.monthlyRevenue.map((m) => m.revenue));
	}

	getBarHeight(revenue: number): number {
		const max = this.getMaxRevenue();
		return max > 0 ? (revenue / max) * 100 : 0;
	}

	/** Payment method code from the API -> translated label (unknown codes are shown as they come) */
	getMethodLabel(method: string): string {
		const key = `dashboard.paymentMethods.methods.${method}`;
		const label = this.translocoService.translate(key);
		return label === key ? method : label;
	}

	getCompletedWidth(stat: PaymentMethodStat): number {
		if (stat.orders === 0) return 0;
		return Math.round((stat.completed / stat.orders) * 100);
	}
}
